import React, { useEffect, useState } from "react";
import {
  AddConsent,
  ViewConsent,
  EditConsent,
  updateConsent
} from "../../services/ConsentMaster/ConsentMaster.js";
import moment from "moment/moment.js";
import { motion } from "framer-motion";
import Loader from "../../components/Loader/Loader.jsx";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils.js";
import { ToastContainer } from "react-toastify";

export default function ConsentMaster() {
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    message: "",
  });

  // ✅ NEW: Fetch consents on mount
  useEffect(() => {
    fetchConsents();
  }, []);

  const fetchConsents = async () => {
    setLoading(true);
    try {
      const res = await ViewConsent(); // No id needed for list/view
      if (res?.data) {
        setConsents(Array.isArray(res.data) ? res.data : [res.data]);
      }
    } catch (err) {
      console.error("Failed to fetch consents", err);
      showErrorToast("Failed to load consents");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- ADD ----------------
  const openAddModal = () => {
    setEditId(null);
    setFormData({ message: "" });
    setModalOpen(true);
  };

  // ---------------- EDIT ----------------
// In openEditModal - use dedicated endpoint
const openEditModal = async (id) => {
  setLoading(true);
  try {
    const res = await EditConsent({ id });  //  Uses /consent/edit/1
    const d = res?.data;
    if (!d) return;
    setEditId(id);
    setFormData({ message: d.message });
    setModalOpen(true);
  } catch (err) {
    showErrorToast("Failed to load consent");
  } finally {
    setLoading(false);
  }
};


  // ---------------- REFRESH AFTER SUBMIT ----------------
  const refreshData = () => {
    setModalOpen(false);
    fetchConsents();
  };

  // ---------------- SUBMIT ----------------
  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editId) {
        await updateConsent({
          id: editId,
          message: formData.message, //  Fixed: direct message, no nested payload
        });
        showSuccessToast("Consent updated successfully");
      } else {
        await AddConsent({ message: formData.message });
        showSuccessToast("Consent added successfully");
      }
      refreshData(); //  Refresh list after success
    } catch (err) {
      console.error("Submit failed", err);
      if (err?.response?.status === 422) {
        const errors = err.response.data.errors;
        const firstError = errors && Object.values(errors)[0] && Object.values(errors)[0][0];
        showErrorToast(firstError || "Validation failed");
      } else {
        showErrorToast("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto mt-16 bg-white rounded-md shadow-lg">
      {loading && <Loader />}
      
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Consent Master</h1>
        <button
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg px-6 py-2 transition-all"
          disabled={loading}
        >
          + Add Consent
        </button>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">ID</th>
              <th className="px-4 py-3 text-left font-semibold">Message</th>
              <th className="px-4 py-3 text-left font-semibold">Updated At</th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {consents.length > 0 ? (
              consents.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-t hover:bg-gray-50 transition-all"
                >
                  <td className="px-4 py-3 font-medium">{doc.id}</td>
                  <td className="px-4 py-3 max-w-md truncate">{doc.message}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {moment(doc.updated_at).format("DD-MM-YYYY hh:mm A")}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => openEditModal(doc.id)}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm font-medium rounded-md px-4 py-1 transition-all disabled:opacity-50"
                      disabled={loading}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                  No consents found. Add one to get started!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      {modalOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex justify-center items-center p-4 z-50"
          onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {editId ? "Edit Consent" : "Add Consent"}
              </h2>
              <button 
                onClick={() => setModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                disabled={loading}
              >
                ✕
              </button>
            </div>

            {/* FORM */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Consent Message
                </label>
                <input
                  type="text"
                  placeholder="Enter consent message"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  disabled={loading}
                />
              </div>

              {/* Submit Button */}
              <button
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-xl py-3 mt-4 transition-all"
                onClick={handleSubmit}
                disabled={loading || !formData.message.trim()}
              >
                {loading ? "Saving..." : (editId ? "Update Consent" : "Add Consent")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      <ToastContainer />
    </div>
  );
}
